<script setup lang="ts">
import { computed, watch } from 'vue';
import {
    BFilterChipDate, BFilterChipSelect, BIcon, BBtn,
} from '@/design-system';
import { useFilterBar, useTranslation } from '@/composables';
import type { GroupingPeriodType } from '@/types/GroupingPeriodType';

type PropsType = {
    activeTab: string;
};

const props = defineProps<PropsType>();

const { getStoreByTabName, resetAllFilters } = useFilterBar();
const { t } = useTranslation();
const currentStore = computed(() => getStoreByTabName(props.activeTab));

const groupingOptions = computed(
    (): Array<{ value: GroupingPeriodType; label: string }> => [
        { value: 'day', label: t('common.grouping.day') },
        { value: 'week', label: t('common.grouping.week') },
        { value: 'month', label: t('common.grouping.month') },
    ],
);

const hasActiveFilters = computed(() => {
    const filters = currentStore.value.filters;

    for (const [key, value] of Object.entries(filters)) {
        if (key === 'dateRange' || key === 'groupedBy') {
            continue;
        }
        if (value !== undefined && value !== null) {
            if (Array.isArray(value) && value.length > 0) {
                return true;
            }
            if (!Array.isArray(value)) {
                return true;
            }
        }
    }

    return false;
});

const handleResetFilters = () => {
    resetAllFilters(props.activeTab);
};

watch(
    () => [currentStore.value.filters.dateRange, currentStore.value.filters.groupedBy, currentStore.value.filters],
    () => {
        currentStore.value.fetchData();
    },
    { deep: true },
);
</script>

<template>
    <div class="b-filter-bar">
        <div class="b-filter-bar__icon">
            <BIcon
                icon="mdi-filter-variant"
                color="primary"
            />
        </div>

        <div class="b-filter-bar__chips">
            <BFilterChipSelect
                v-model="currentStore.filters.groupedBy"
                :items="groupingOptions"
            />
            <BFilterChipDate
                v-model="currentStore.filters.dateRange"
                :presets="['day', 'week', 'month']"
            />
        </div>

        <div class="b-filter-bar__filters">
            <slot
                name="filters"
                :active-tab="activeTab"
            />
        </div>

        <div class="b-filter-bar__actions">
            <BBtn
                v-if="hasActiveFilters"
                variant="text"
                color="error"
                icon
                @click="handleResetFilters"
            >
                <BIcon icon="mdi-filter-off" />
            </BBtn>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.b-filter-bar {
    display: flex;
    align-items: flex-center;
    gap: 16px;
    width: 100%;
    padding: 8px 16px;
    flex-wrap: wrap;
}

.b-filter-bar__icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding-bottom: 8px;
}

.b-filter-bar__chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.b-filter-bar__filters {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex: 1;
    overflow-x: auto;
    flex-wrap: nowrap;
    min-width: 0;
    padding-block: 0.5rem;
}

.b-filter-bar__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
    padding-bottom: 4px;
}

@media (max-width: 768px) {
    .b-filter-bar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding: 12px;
    }

    .b-filter-bar__icon {
        display: none;
    }

    .b-filter-bar__chips {
        flex-wrap: wrap;
    }

    .b-filter-bar__filters {
        flex-direction: column;
        align-items: stretch;
        flex-wrap: wrap;
        overflow-x: visible;
    }

    .b-filter-bar__actions {
        margin-left: 0;
        justify-content: flex-end;
    }
}
</style>
